import React from 'react'
import styled, { css } from 'styled-components'

import { StyledTheme } from 'theme'
import svgIcon from './svg-icons/icon.svg'
import { OutlineStar } from './svg-icons/outline-icon'
import { ThemeSpace } from 'theme/spaces'

export const IconSizes = {
  SMALL: 'small',
  DEFAULT: 'default',
  LARGE: 'large',
  EXTRA_SMALL: 'xSmall',
} as const

export type IconSize = (typeof IconSizes)[keyof typeof IconSizes]

const getStarSize = (size: IconSize): ThemeSpace => {
  switch (size) {
    case 'small':
      return 's20'
    case 'xSmall':
      return 's16'
    case 'large':
      return 's48'
    default:
      return 's32'
  }
}

export const StyledOutlineStar = styled(OutlineStar).attrs(
  ({ theme, size }: { theme: StyledTheme; size: IconSize }) => ({
    size: theme.space[getStarSize(size)],
  })
)<{ size: IconSize }>`
  visibility: hidden; // show outline icon only on focus-visible (controlled by Rating-component)

  position: absolute;
  top: 0;
  left: 0;

  color: ${({ theme }) => theme.color.primary70};
`

export const Star = styled.div<{ disabled: boolean; size: IconSize }>`
  position: absolute;
  top: 0;
  left: 0;

  mask: ${`url(${svgIcon})`};
  mask-size: cover;
  width: ${({ theme, size }) => theme.space[getStarSize(size)]};
  height: ${({ theme, size }) => theme.space[getStarSize(size)]};
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.color.grey10 : theme.color.grey30};
`

const DisabledGradient = css<{ $percentage: number }>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.color.primary30} ${({ $percentage }) => $percentage}%,
    transparent ${({ $percentage }) => $percentage}%
  );
`

const NoOwnRatingGradient = css<{ $percentage: number }>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.color.primary70} ${({ $percentage }) => $percentage}%,
    transparent ${({ $percentage }) => $percentage}%
  );
`

const OwnRatingGradient = css<{
  $percentage: number;
  disabled: boolean;
  $isHovered: boolean;
  size: IconSize;
  $hasOwnRating: boolean;
}>`
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.color.primary110}
      ${({ $percentage }) => $percentage}%,
    transparent ${({ $percentage }) => $percentage}%
  );
`

export const FilledStar = styled(Star)<{
  disabled: boolean
  $isHovered: boolean
  size: IconSize
  $percentage: number
  $hasOwnRating: boolean
}>`
  background-color: ${({ theme, $isHovered }) =>
    $isHovered && theme.color.primary120};

  ${({ $isHovered, $hasOwnRating }) =>
    !$isHovered && $hasOwnRating && OwnRatingGradient};
  ${({ $isHovered, $hasOwnRating }) =>
    !$isHovered && !$hasOwnRating && NoOwnRatingGradient};
  ${({ disabled }) => disabled && DisabledGradient};
}
`

export const StarBox = styled.div<{ size: IconSize }>`
  position: relative;

  padding: 0;
  width: ${({ theme, size }) => theme.space[getStarSize(size)]};
  height: ${({ theme, size }) => theme.space[getStarSize(size)]};
`

interface IconProps {
  size: IconSize
  percentage: number // percentage of how filled the star is. E.g. if the rating is 3.2, 4th start is 20% filled (percentage = 20).
  isHovered: boolean
  isHoveringStars: boolean // if user is hovering one of the stars
  hasOwnRating: boolean
  disabled?: boolean
}

export const StarIcon = ({
  size,
  percentage,
  isHovered,
  isHoveringStars,
  hasOwnRating,
  disabled = false,
}: IconProps) => {
  const isFilledHovered = !disabled && isHovered
  const showFillStar = !isHoveringStars || isHovered

  return (
    <StarBox size={size} aria-hidden>
      <StyledOutlineStar size={size} />
      <Star disabled={disabled} size={size} />
      {showFillStar && (
        <FilledStar
          disabled={disabled}
          $isHovered={isFilledHovered}
          size={size}
          $percentage={percentage}
          $hasOwnRating={hasOwnRating}
        />
      )}
    </StarBox>
  )
}
